const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/PDFViewer/PDFViewerProvider.tsx';
import React, { createContext, ReactNode, useContext, useRef } from 'react';
import { usePDFDocumentController } from './usePDFDocumentController';

export type PDFViewerContextValue = ReturnType<typeof usePDFDocumentController> & {
    /** Attached by PDFDocument (drawing, paging, scale, save); used by controller actions. */
    documentRef: React.MutableRefObject<any>;
};

const PDFViewerContext = createContext<PDFViewerContextValue | null>(null);

interface Props {
    /** Used for the download filename: `${documentTitle}.pdf`. */
    documentTitle?: string;
    children: ReactNode;
}

/**
 * Single source of truth for one PDF viewer instance.
 *
 * Owns the documentRef (the imperative handle PDFDocument exposes) and all
 * controller state via usePDFDocumentController, called exactly once here.
 *
 * PDFDocument writes state through the setDocumentState / setDocumentChanged
 * callbacks in the context value. Any number of toolbars (footer, floating,
 * dialog title bar) read the same state via usePDFViewer(). Because the state
 * lives here and not inside the toolbars, toolbars can mount, unmount, and
 * swap on breakpoint changes without losing or desyncing the mode.
 */
export const PDFViewerProvider = ({ documentTitle, children }: Props) => {
    const documentRef = useRef<any>(null);

    const ctl = usePDFDocumentController({ documentRef, documentTitle });

    // Re-render note: every state commit re-renders all consumers, which is fine
    // because everything flowing through here is low-frequency (mode toggles, page
    // changes, committed zoom). The per-frame pinch preview never touches this
    // provider — usePdfPinchZoom applies a CSS transform and only commits scale
    // on touchend (see PDFDocument).
    return <PDFViewerContext.Provider value={{ ...ctl, documentRef }}>{children}</PDFViewerContext.Provider>;
};

export const usePDFViewer = (): PDFViewerContextValue => {
    const value = useContext(PDFViewerContext);
    if (!value) {
        throw new Error('usePDFViewer must be used inside a <PDFViewerProvider>');
    }
    return value;
};
